import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { SortableSound } from './SortableSound';
import type { Sound } from '../types/database';
import { useTranslation } from 'react-i18next';

interface SortableSoundListProps {
  sounds: Sound[];
  onSoundsReorder: (sounds: Sound[]) => void;
  playingSound: string | null;
  setPlayingSound: (id: string | null) => void;
  isMember: boolean;
  onEdit: (sound: Sound) => void;
  onDelete: (id: string) => void;
}

export function SortableSoundList({
  sounds,
  onSoundsReorder,
  playingSound,
  setPlayingSound,
  isMember,
  onEdit,
  onDelete
}: SortableSoundListProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Sound[]>(sounds);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        
        const newItems = arrayMove(items, oldIndex, newIndex);
        onSoundsReorder(newItems);
        return newItems;
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(sound => sound.id)}
        strategy={verticalListSortingStrategy}
      >
        {items.length > 0 ? (
          items.map((sound, index) => (
            <SortableSound
              key={sound.id}
              sound={sound}
              playingSound={playingSound}
              setPlayingSound={setPlayingSound}
              isMember={isMember}
              onEdit={onEdit}
              onDelete={onDelete}
              index={index}
              isFirst={index === 0}
              isLast={index === sounds.length - 1}
            />
          ))
        ) : (
          <div className="text-center py-6">
            <p className="text-light/60">{t('bands.sounds.noSounds')}</p>
            {isMember && (
              <button
                onClick={() => onEdit({
                  id: '',
                  band_id: '',
                  title: '',
                  url: '',
                  provider: 'spotify',
                  created_at: '',
                  updated_at: ''
                })}
                className="mt-4 underline"
              >
                {t('bands.sounds.addFirstSound')}
              </button>
            )}
          </div>
        )}
      </SortableContext>
    </DndContext>
  );
}